/**
 * The contract between the Hatch API and the Hatch frontend.
 *
 * Both sides import from here so a change to the activity vocabulary is a
 * type error rather than a silently desynced UI. Nothing in this file may
 * import from `api/` or `frontend/` — it is the shared floor, not a bridge.
 */

// ---------------------------------------------------------------------------
// Session lifecycle
// ---------------------------------------------------------------------------

/**
 * Session states, mirroring the state machine in the plan's design section.
 *
 * `draft` is the only state that accepts a prompt. Every terminal state
 * releases the agent lease. There is deliberately no state that returns to
 * accepting input — the read-only guarantee is enforced by the absence of a
 * transition, not by a check.
 */
export const SESSION_STATES = [
  "draft",
  "queued",
  "provisioning",
  "building",
  "deploying",
  "succeeded",
  "failed",
  "expired",
  "rejected",
  "cancelled",
] as const;

export type SessionState = (typeof SESSION_STATES)[number];

/** States from which no further transition is legal. */
export const TERMINAL_SESSION_STATES = [
  "succeeded",
  "failed",
  "expired",
  "rejected",
  // Reachable only by the sender asking for it — today that means texting STOP
  // to the Twilio number. Distinct from `expired` because the difference is the
  // whole point: nothing went wrong, and nobody should be told it did.
  "cancelled",
] as const satisfies readonly SessionState[];

export type TerminalSessionState = (typeof TERMINAL_SESSION_STATES)[number];

export function isTerminalState(state: SessionState): state is TerminalSessionState {
  return (TERMINAL_SESSION_STATES as readonly SessionState[]).includes(state);
}

// ---------------------------------------------------------------------------
// Frontend framework
// ---------------------------------------------------------------------------

/**
 * The frontend framework a session's site is built with.
 *
 * `xanots init` scaffolds either, and the agent image bakes one dependency-
 * installed template per entry (agent-runner/Dockerfile). Adding a third means
 * baking a third template and paying its build time on every image, so this
 * list is deliberately short rather than open.
 *
 * Order matters only in that the first entry is the default — see
 * `normalizeFramework`.
 */
export const FRAMEWORKS = ["react", "svelte"] as const;

export type Framework = (typeof FRAMEWORKS)[number];

/** What a caller gets when it says nothing. */
export const DEFAULT_FRAMEWORK: Framework = FRAMEWORKS[0];

/**
 * Coerce an untrusted value to a framework, falling back rather than throwing.
 *
 * Deliberately total. Three callers send no framework at all and must keep
 * working: an SMS intake with no affordance for one, `xano hatch` (an
 * out-of-repo consumer carrying a hand-written copy of this contract — see
 * docs/cli.md), and any web bundle cached from before the field existed. A
 * fourth case is the interesting one: a FUTURE client sending `"vue"` should
 * get a working React site, not a 400 telling it a value it believes in is
 * invalid.
 *
 * So the only thing an unrecognized value costs is the choice, never the
 * session. Case-sensitive on purpose — every other union in this file compares
 * exactly, and `"React"` is a client bug worth surfacing as a silent default
 * rather than papering over here and nowhere else.
 */
export function normalizeFramework(value: unknown): Framework {
  return (FRAMEWORKS as readonly unknown[]).includes(value)
    ? (value as Framework)
    : DEFAULT_FRAMEWORK;
}

// ---------------------------------------------------------------------------
// Deep Think
// ---------------------------------------------------------------------------

/**
 * Whether the build gets Hatch's full craft brief on top of the base prompt.
 *
 * ON, for every build, and no longer a choice anyone is asked to make.
 *
 * It was a toggle on the reasoning that the base prompt already produces a
 * working site and the brief costs roughly a third more prompt and a
 * noticeably longer build — a real trade, so offer it. What that reasoning
 * missed is who is standing there. A visitor at a booth has one build in them
 * and no way to know what the toggle buys, so the choice was being made by
 * whether someone happened to press an unfamiliar control, and the version of
 * Hatch most people saw was the cheaper one. The brief is what makes a
 * generated site look considered rather than generated — real column types and
 * indexes, paged list endpoints, three states on every fetching screen, a
 * chosen palette — which is the entire thing being demonstrated.
 *
 * So the cost is now simply paid. The flag itself stays on the contract, the
 * session row, and the prompt builder: `false` still turns the brief off for
 * anything that asks, and `deepThinkFor` still emits nothing for it. What is
 * gone is the UI that asked. See `deepThinkFor` in api/src/agent/prompt.ts for
 * what the brief actually adds.
 *
 * A boolean rather than a union of levels. There is one brief, and a second
 * tier would have to be a genuinely different document rather than a dial —
 * make it a union then, and normalize the same way `normalizeFramework` does.
 */
export const DEFAULT_DEEP_THINK = true;

/**
 * Coerce an untrusted value to the flag, falling back rather than throwing.
 *
 * Total, for exactly the reasons `normalizeFramework` is: SMS has no affordance
 * for it, `xano hatch` is an out-of-repo consumer carrying a hand-written copy
 * of this contract and does not send it, and a web bundle cached from before
 * the field existed sends nothing either. All three must keep working, and all
 * three should land on the default rather than a 400.
 *
 * This used to read `value === true`, which was correct only while the default
 * was `false` — the two happened to agree. They no longer do, and that spelling
 * would now answer `false` for every client that sends nothing at all, which is
 * SMS, the CLI, and any cached bundle: precisely the callers the paragraph
 * above promises will land on the default. The absent case has to be named.
 *
 * An explicit `false` is still honoured, because turning the brief off is a
 * capability that outlived its control. Anything that is not a boolean —
 * `"true"`, `1`, `"on"` — falls back to the default rather than being coerced:
 * each is a client that thinks it is sending a boolean and is not, and a silent
 * default is a better place to discover that than a build that is quietly the
 * wrong shape. JSON has a boolean; a JSON body has no excuse.
 */
export function normalizeDeepThink(value: unknown): boolean {
  return typeof value === "boolean" ? value : DEFAULT_DEEP_THINK;
}

/**
 * Legal transitions. The service consults this rather than open-coding
 * `if` chains, so an illegal transition is a single throw site and the
 * machine is readable in one place.
 */
export const SESSION_TRANSITIONS: Readonly<Record<SessionState, readonly SessionState[]>> = {
  draft: ["queued"],
  // `expired` is reachable from queued because a session can be stranded here:
  // if the API dies between accepting the prompt and acquiring a lease, nothing
  // ever moves it forward and the reaper must still be able to close it out.
  // `cancelled` is reachable from every non-terminal state, including `queued`:
  // a sender who texts STOP before a lease is acquired is asking for the same
  // thing as one who texts it mid-build, and refusing them because the pool was
  // slow would be an implementation detail leaking into the product.
  queued: ["provisioning", "rejected", "expired", "cancelled"],
  provisioning: ["building", "failed", "expired", "cancelled"],
  building: ["deploying", "failed", "expired", "cancelled"],
  deploying: ["succeeded", "failed", "expired", "cancelled"],
  succeeded: [],
  failed: [],
  expired: [],
  rejected: [],
  cancelled: [],
};

export function canTransition(from: SessionState, to: SessionState): boolean {
  return SESSION_TRANSITIONS[from].includes(to);
}

// ---------------------------------------------------------------------------
// Build phases (the user-facing progress spine)
// ---------------------------------------------------------------------------

/**
 * Coarse phases shown on the timeline. Deliberately fewer than the session
 * states: the user cares about what the builder is *doing*, not about lease
 * bookkeeping. Phases only ever advance — see `PHASE_ORDER`.
 *
 * There is no `designing`. Hatch scaffolds the project itself and the agent
 * goes straight to writing files, so nothing ever reported it — it sat in the
 * timeline as a step that lit up only as something to pass through on the way
 * to `backend`. A phase no build spends time in is a promise the header does
 * not keep.
 */
export const BUILD_PHASES = [
  "queued",
  "scaffolding",
  "backend",
  "frontend",
  "deploying",
  "done",
] as const;

export type BuildPhase = (typeof BUILD_PHASES)[number];

/** Index used to enforce monotonic phase advance in the projector and the UI. */
export const PHASE_ORDER: Readonly<Record<BuildPhase, number>> = Object.freeze(
  Object.fromEntries(BUILD_PHASES.map((p, i) => [p, i])) as Record<BuildPhase, number>,
);

// ---------------------------------------------------------------------------
// Activity — the projected, user-facing event stream
// ---------------------------------------------------------------------------

/**
 * The vocabulary the projector maps raw agent events onto.
 *
 * `narration` is the catch-all: an unrecognized raw event degrades into a
 * generic narration rather than being dropped, so an upstream schema change
 * costs fidelity instead of going silent.
 */
export const ACTIVITY_KINDS = [
  "phase",
  "narration",
  "artifact",
  "log",
  "heartbeat",
  "terminal",
] as const;

export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

interface ActivityBase {
  /** Monotonic per-session sequence. Doubles as the SSE `Last-Event-ID`. */
  seq: number;
  /** Epoch milliseconds, server-assigned. */
  at: number;
  kind: ActivityKind;
}

/** The build moved to a new phase. Emitted only on genuine advance. */
export interface PhaseActivity extends ActivityBase {
  kind: "phase";
  phase: BuildPhase;
}

/** One human-readable line describing what the builder is doing right now. */
export interface NarrationActivity extends ActivityBase {
  kind: "narration";
  message: string;
}

/**
 * A file the agent created or changed — or failed to.
 *
 * `failed` is a correction, not an operation: the projector emits `modified`
 * when the write CALL arrives (the reliable signal — see `filePathOf` in
 * api/src/activity/projector.ts), and if the tool's observation then reports an
 * error, the relay follows up with a `failed` artifact for the same path. A
 * consumer keeping counts nets the pair out; a consumer rendering rows shows
 * the failure instead of a write that never landed. Historical events predate
 * the value and simply never carry it.
 */
export interface ArtifactActivity extends ActivityBase {
  kind: "artifact";
  path: string;
  action: "created" | "modified" | "deleted" | "failed";
}

/**
 * Filtered stdout/stderr, rendered under the step it belongs to.
 *
 * `role` is what lets the frontend show a command and its output as one thing:
 * the projector echoes an executed command as a log line, and without a marker
 * that echo is indistinguishable from the output it produced. Absent means
 * output — the common case, and the safe reading for any emitter that predates
 * the field.
 */
export interface LogActivity extends ActivityBase {
  kind: "log";
  stream: "stdout" | "stderr";
  line: string;
  role?: "input" | "output";
}

/**
 * Synthetic activity emitted during known-quiet stretches so a long
 * `npm install` or model turn does not read as a stall.
 *
 * Everything here is true and observed — elapsed time, the last real action,
 * the running artifact count. A heartbeat must never invent a phase advance
 * or an artifact.
 */
export interface HeartbeatActivity extends ActivityBase {
  kind: "heartbeat";
  phase: BuildPhase;
  /** Milliseconds spent in the current phase so far. */
  elapsedInPhaseMs: number;
  /** The most recent genuine narration, repeated for context. */
  lastAction: string;
  /**
   * NET successful writes: a write whose observation reported an error is
   * corrected back out (see the `failed` artifact action above), and the
   * emitter clamps a transient negative to zero for display.
   */
  filesWritten: number;
}

/** The build finished. Exactly one of these is ever emitted per session. */
export interface TerminalActivity extends ActivityBase {
  kind: "terminal";
  outcome: TerminalSessionState;
  /** Present only when `outcome === "succeeded"`. */
  siteUrl?: string;
  /** Present on any non-success outcome. User-facing, never a raw stack trace. */
  message?: string;
}

/**
 * An activity before the server stamps it.
 *
 * Distributive by construction: a plain `Omit<Activity, "seq" | "at">` over a
 * union collapses to the keys every member shares, which would drop `outcome`,
 * `phase`, `message`, and the rest.
 */
export type ActivityDraft<T = Activity> = T extends unknown ? Omit<T, "seq" | "at"> : never;

export type Activity =
  | PhaseActivity
  | NarrationActivity
  | ArtifactActivity
  | LogActivity
  | HeartbeatActivity
  | TerminalActivity;

// ---------------------------------------------------------------------------
// HTTP + SSE surface
// ---------------------------------------------------------------------------

/**
 * `POST /api/sessions` — body, and every field of it is optional.
 *
 * The whole body is optional too: three intakes send none at all. Anything
 * unrecognized is coerced rather than rejected (see `normalizeFramework`), so
 * this interface documents what a caller MAY say, never what it must.
 */
export interface CreateSessionRequest {
  /**
   * Which frontend to build. Absent, null, or unrecognized → the default.
   *
   * Additive by design: `xano hatch` carries its own hand-written copy of this
   * contract and nothing in this repo typechecks it (docs/cli.md), so a field
   * a released CLI does not send must keep working forever.
   */
  framework?: Framework;
  /**
   * Whether to hand the agent Hatch's full craft brief. Absent or anything
   * other than `true` → the default (off).
   *
   * Additive by design, same as `framework`: `xano hatch` carries its own
   * hand-written copy of this contract and nothing here typechecks it
   * (docs/cli.md), so a field a released CLI does not send must keep working
   * forever.
   */
  deepThink?: boolean;
}

/** `POST /api/sessions` */
export interface CreateSessionResponse {
  sessionId: string;
  state: SessionState;
  /**
   * A link that opens this session in a browser, present only when the caller
   * asked for one with `?watch=1`.
   *
   * The website does not ask: a browser already holds the session cookie, and
   * minting a shareable bearer link nobody requested is not free. A client
   * whose session lives somewhere the user's browser is not — `xano hatch` —
   * asks, and prints what comes back.
   *
   * Absolute, and built by the server from `APP_URL`. The API and the SPA are
   * the same origin in production but not in development, so a client
   * concatenating its own base with `/s/<code>` would print a link into the
   * API that renders nothing.
   */
  watchUrl?: string;
}

/** `POST /api/sessions/:id/prompt` */
export interface SubmitPromptRequest {
  prompt: string;
}

export interface SubmitPromptResponse {
  sessionId: string;
  state: SessionState;
}

// ---------------------------------------------------------------------------
// Backend primitives
// ---------------------------------------------------------------------------

/**
 * Every authorable Xano primitive a Hatch build can register, in the order a
 * reader should meet them.
 *
 * This list is the catalog from `@xanots/sdk`'s own `object-kinds.md`, keyed by
 * the plural noun the screen says rather than by the factory name — a visitor
 * reads "3 tables", not "3 `table()` defs". `api/src/agent/backend-counts.ts`
 * maps each key to the `register*` method that ships it, and nothing else may
 * invent a key: a count with no entry here has no label and no place in the
 * order, so it would arrive on the wire and render as nothing.
 *
 * The order is deliberate and not alphabetical. The first three are what a
 * backend IS to someone who has never opened Xano — the data, the endpoints
 * over it, and the logic they share — and they are the three the done screen
 * gives full tiles to. Everything after them is real and secondary, grouped by
 * neighbourhood: the request-shaping kinds, then AI, then realtime, then the
 * rest.
 *
 * Adding a kind here is safe for every existing consumer (see the drift note in
 * docs/cli.md); removing or renaming one is not.
 */
export const BACKEND_OBJECT_KINDS = [
  "tables",
  "apis",
  "functions",
  "apiGroups",
  "addons",
  "middleware",
  "triggers",
  "agents",
  "tools",
  "mcpServers",
  "knowledge",
  "realtimeServers",
  "realtimeChannels",
  "realtimeMessages",
  "tasks",
  "workflowTests",
  "microservices",
] as const;

export type BackendObjectKind = (typeof BACKEND_OBJECT_KINDS)[number];

/**
 * What a build registered, per primitive.
 *
 * Every key is independently optional and ABSENT MEANS UNKNOWN, never none. A
 * build whose source could not be read carries no counts at all; a build with
 * no functions carries `functions: 0`. A consumer that renders these must show
 * nothing for an absent one rather than a zero — the whole point of the
 * distinction is that "0 tables" under a working backend is worse than saying
 * less.
 */
export type BackendObjects = Partial<Record<BackendObjectKind, number>>;

/**
 * How each kind is named on screen, singular and plural.
 *
 * Shared rather than per-skin because these are the names of things that exist,
 * not a tone: a skin that called `apis` "routes" while another called them
 * "endpoints" would be describing the same tenant two ways.
 *
 * `apis` is "endpoint" rather than "API" on purpose — the count is of queries,
 * not of API groups, and `apiGroups` is its own kind right below it.
 */
export const BACKEND_OBJECT_LABELS: Record<BackendObjectKind, { one: string; many: string }> = {
  tables: { one: "Postgres table", many: "Postgres tables" },
  apis: { one: "API endpoint", many: "API endpoints" },
  functions: { one: "function", many: "functions" },
  apiGroups: { one: "API group", many: "API groups" },
  addons: { one: "addon", many: "addons" },
  middleware: { one: "middleware", many: "middleware" },
  triggers: { one: "trigger", many: "triggers" },
  agents: { one: "AI agent", many: "AI agents" },
  tools: { one: "agent tool", many: "agent tools" },
  mcpServers: { one: "MCP server", many: "MCP servers" },
  knowledge: { one: "knowledge doc", many: "knowledge docs" },
  realtimeServers: { one: "realtime server", many: "realtime servers" },
  realtimeChannels: { one: "realtime channel", many: "realtime channels" },
  realtimeMessages: { one: "realtime handler", many: "realtime handlers" },
  tasks: { one: "scheduled task", many: "scheduled tasks" },
  workflowTests: { one: "workflow test", many: "workflow tests" },
  microservices: { one: "microservice", many: "microservices" },
};

/** "1 table", "3 Postgres tables" — the one place plurals are decided. */
export function describeBackendObject(kind: BackendObjectKind, count: number): string {
  const label = BACKEND_OBJECT_LABELS[kind];
  return `${String(count)} ${count === 1 ? label.one : label.many}`;
}

/**
 * Whether this build put anything on a websocket.
 *
 * Asked as one question because that is how it reads on the done screen — a
 * "realtime" chip is true when a server, a channel, or a handler exists, and a
 * build cannot have a channel without the server that owns it anyway.
 */
export function hasRealtime(objects: BackendObjects | null | undefined): boolean {
  if (!objects) return false;
  return (
    (objects.realtimeServers ?? 0) > 0 ||
    (objects.realtimeChannels ?? 0) > 0 ||
    (objects.realtimeMessages ?? 0) > 0
  );
}

/** `GET /api/sessions/:id` — the snapshot the build view opens against. */
export interface SessionSnapshot {
  sessionId: string;
  state: SessionState;
  phase: BuildPhase;
  prompt: string;
  /**
   * The frontend framework this session builds with.
   *
   * Fixed at creation, not at prompt submission: the relay needs it to pick a
   * template before the agent's conversation exists. Always present on the
   * wire — a session that never stated one carries the default rather than
   * omitting the field, so a reader never has to know where the default lives.
   */
  framework: Framework;
  /**
   * Whether this session was built with Deep Think.
   *
   * Fixed at creation alongside `framework`, and always present on the wire —
   * a session that never stated one carries `false` rather than omitting the
   * field, so a reader never has to know where the default lives.
   */
  deepThink: boolean;
  createdAt: number;
  /** Present once the session reaches `succeeded`. */
  siteUrl?: string;
  /**
   * The build identity `siteUrl` is expected to be serving — see
   * `buildIdMarker`. Absent when the stamp could not be injected, which is the
   * signal to verify the site the way Hatch did before identities existed.
   */
  siteBuildId?: string;
  /**
   * The Xano backend behind `siteUrl`, when Hatch could identify it.
   *
   * Deliberately just the tenant name — no workspace, no instance host, no
   * expiry. The browser's only decision is whether to offer a way into the
   * backend, and the name answers that while carrying nothing a visitor could
   * act on directly. Everything needed to actually open it stays server-side,
   * because opening it costs a credential the browser must never hold.
   *
   * Absent when the deploy summary named no ephemeral tenant. That is a normal
   * outcome, not a fault — the site still works, there is simply no door.
   */
  xanoBackend?: {
    tenant: string;
    /**
     * What the deploy registered, counted off the workspace source Hatch just
     * shipped (`api/src/agent/backend-counts.ts`).
     *
     * Every field is independently optional and ABSENT MEANS UNKNOWN, never
     * none. A build whose source could not be read carries no counts at all; a
     * build with no functions carries `functions: 0`. A consumer that renders
     * these must show nothing for an absent one rather than a zero — the whole
     * point of the distinction is that "0 tables" under a working backend is
     * worse than saying less.
     */
    objects?: BackendObjects;
  };
  /**
   * Documents the static host published — pages, to whoever reads the screen.
   *
   * Absent when the deploy did not report a count, which is the same build
   * whose config never reached a document. Same absent-means-unknown rule as
   * the object counts above.
   */
  sitePages?: number;
  /** Present on a non-success terminal state. */
  failureMessage?: string;
  /**
   * The short code a visitor reads aloud at the booth so staff can pull this
   * build up.
   *
   * Absent for every session that did not arrive by SMS, which is most of
   * them — a website visitor is standing in front of you. A consumer must
   * render nothing at all when it is absent rather than an empty slot.
   *
   * This is the only code on the contract. `watchCode` is a bearer capability
   * and never appears here; this one opens nothing without the staff token.
   */
  boothCode?: string;
}

/**
 * `GET /api/sessions/:id/meta` — everything about one run, in one call.
 *
 * A superset of `SessionSnapshot`, and deliberately a separate type rather
 * than an extension of it: the snapshot is what the browser's build view
 * needs and is kept deliberately narrow (no watch code, no phone number, no
 * internal failure detail). This is the debugging view, read by whoever holds
 * the session id, and the two audiences should be free to diverge.
 *
 * Additive to the wire in every direction — `xano hatch` carries a hand-written
 * copy of this file's activity vocabulary and nothing here typechecks it, so
 * fields may be added but not renamed. See docs/cli.md.
 */
export interface SessionMeta {
  sessionId: string;
  state: SessionState;
  phase: BuildPhase;
  framework: Framework;
  /** Whether this run was given the craft brief. See `agentPrompt` for it. */
  deepThink: boolean;
  /** The prompt as submitted, trimmed and bounded by `MAX_PROMPT_LENGTH`. */
  prompt: string;
  /**
   * The instructions the agent was actually handed, as composed and sent.
   *
   * Absent for a session that never ran, and for any session that predates the
   * column. Recorded rather than recomputed, so it describes THIS run rather
   * than what the current template would produce.
   */
  agentPrompt?: string;
  createdAt: number;
  /** When the one prompt landed. Absent while the session is still `draft`. */
  promptAt?: number;
  /** When the session reached a terminal state. Absent while it is running. */
  terminalAt?: number;
  siteUrl?: string;
  siteBuildId?: string;
  /**
   * The ephemeral tenant, and what the build registered in it.
   *
   * `objects` is carried here as well as on the snapshot because this type is
   * built by spreading one — it was always on the wire and merely undeclared,
   * and a contract that under-describes its own response is one a consumer
   * reads by experiment.
   */
  xanoBackend?: { tenant: string; objects?: BackendObjects };
  /** Documents the static host published. Same absent-means-unknown rule. */
  sitePages?: number;
  /**
   * The short code a visitor reads aloud at the booth, for SMS builds.
   *
   * Absent for every session started from the website. It opens nothing on its
   * own — see `StaffBuild` for the surface that consumes it.
   */
  boothCode?: string;
  failureMessage?: string;
  /**
   * The projected activity log, oldest first. Absent when the caller asked for
   * `?log=0`, and replaced by `rawActivity` when they asked for `?raw=1`.
   */
  activity?: Activity[];
  /**
   * The unprojected agent events, oldest first, each paired with its sequence
   * number. Present only under `?raw=1`.
   *
   * The shape is the agent server's, not Hatch's, so it is typed as unknown
   * and redacted as text on the way out. This is the debugging view of last
   * resort — it includes events that projected to nothing, which is the entire
   * reason to reach for it.
   */
  rawActivity?: Array<{ seq: number; at: number; raw: unknown }>;
  /**
   * Pass as `?since=` to fetch the next page of the log.
   *
   * Present only when the page came back full and there is more behind it, so
   * its absence is the stop condition — a caller loops until it is gone rather
   * than comparing lengths against a limit it may not have set.
   */
  nextSince?: number;
}

/** Log page size when the caller does not say. */
export const META_LOG_LIMIT_DEFAULT = 500;

/**
 * Log page ceiling.
 *
 * A fifteen-minute build produces thousands of events, and `/meta` is
 * unauthenticated — an unbounded body is both a bad response and a cheap way
 * to make the API do a lot of work. A caller who wants everything pages for it.
 */
export const META_LOG_LIMIT_MAX = 2000;

/**
 * The `window` global a deploy's identity is published as.
 *
 * Hatch runs more than one deploy against a session's static host — the
 * provisioning warm-up publishes the untouched template long before the user's
 * site exists — and every one of them carries the generic markers a readiness
 * probe would otherwise key off. "A xanots deploy landed here" therefore
 * stopped being the same question as "the site the user asked for is serving",
 * and this is what tells them apart.
 */
export const BUILD_ID_GLOBAL = "HATCH_BUILD";

/**
 * The exact text a stamped document carries, for both readiness gates to look
 * for.
 *
 * Built rather than pattern-matched, and matched as an exact substring, because
 * the identities in play are deliberately near-misses of each other: the
 * warm-up's stamp is the build's own identity behind a prefix. Including the
 * assignment's opening quote is what makes `="abc"` fail to match `="warm.abc"`
 * — a looser check would accept the placeholder as the finished site, which is
 * the entire failure this exists to catch.
 *
 * Mirrors XanoTS's injection: it writes `window[<json key>]=<json value>` into
 * the root document's `<head>`, ahead of any bundle.
 */
export function buildIdMarker(buildId: string): string {
  return `window[${JSON.stringify(BUILD_ID_GLOBAL)}]=${JSON.stringify(buildId)}`;
}

/**
 * `GET /api/sessions/:id/site` — is the deployed site actually serving yet?
 *
 * A deploy returns a URL before the edge is routing it, so the frame would
 * otherwise mount against a host that does not resolve. The browser cannot
 * answer this itself: the site is cross-origin, so a failed frame and a
 * rendered one are indistinguishable from Hatch's side. The server can, because
 * it makes a real request and sees a real status code.
 */
export interface SiteReadyResponse {
  ready: boolean;
  /**
   * The build identity the site is expected to be serving, so the browser can
   * apply the same test to the response *it* is given.
   *
   * The server's answer is necessary and not sufficient: it is a different
   * network path with a different cache, and it goes on being right while this
   * browser is still being handed a cached warm-up. Absent when the deploy
   * could not be stamped — see `SessionSnapshot.siteBuildId`.
   */
  buildId?: string;
}

/**
 * Error envelope. `code` is a stable machine key the frontend branches on;
 * `message` is user-facing prose. Never carries internal detail.
 */
export const API_ERROR_CODES = [
  "session_not_found",
  "prompt_already_submitted",
  "prompt_empty",
  "pool_exhausted",
  "credential_unavailable",
  // Booth staff surface. `build_not_redeployable` is deliberately its own code
  // rather than an `internal`: it is the normal state of every build made
  // before Hatch started keeping source, and the dashboard renders it as an
  // explanation rather than as an error.
  "build_not_redeployable",
  "redeploy_failed",
  // The redeployed tenant is gone. Its own code because the remedy is the
  // opposite of the one an error suggests: a retry can never succeed, and
  // redeploying always can. An ephemeral lives about an hour, so this is the
  // ordinary state of any deploy staff come back to later — see the 404 row of
  // the symptom table in docs/xano-handoff.md.
  "deploy_expired",
  "internal",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export interface ApiError {
  code: ApiErrorCode;
  message: string;
}

/**
 * What the booth dashboard shows for one code.
 *
 * Not part of the public API surface documented in docs/api.md — every route
 * returning this is behind `HATCH_STAFF_TOKEN`. It is here rather than in the
 * API workspace because the dashboard is an out-of-repo consumer carrying a
 * hand-written copy, and this is the file that copy is a copy OF. The same
 * drift rule as the CLI's activity contract applies: adding a field is safe,
 * renaming or removing one silently breaks a dashboard nothing here typechecks.
 */
export interface StaffBuild {
  sessionId: string;
  /** The code that resolved this build, echoed back as it was matched. */
  boothCode: string | null;
  state: SessionState;
  framework: Framework;
  /** Whether the visitor asked for the craft brief. */
  deepThink: boolean;
  /** What the visitor asked for. The first thing staff read out. */
  prompt: string;
  /**
   * How long the build took, end to end, in milliseconds.
   *
   * Computed here rather than left to the dashboard: it is the difference of
   * two timestamps, and a headline number derived client-side is one that can
   * disagree with itself between two screens. Absent for a build that never
   * reached a terminal state.
   */
  buildMs?: number;
  createdAt: number;
  /** What the ORIGINAL build published. Expired by the time staff see it. */
  originalSiteUrl?: string;
  /** What the original build registered, when Hatch could establish it. */
  objects?: BackendObjects;
  sitePages?: number;
  failureMessage?: string;
  /**
   * Whether this build's source was captured and can be redeployed.
   *
   * False for every build made before capture shipped, which the dashboard
   * must render as its own state rather than as a failure.
   */
  redeployable: boolean;
  /** The live booth environment, when staff have deployed one. */
  deploy?: StaffDeploy;
}

/** An environment a booth redeploy created and staff are demoing now. */
export interface StaffDeploy {
  siteUrl: string;
  /** Absent when the deploy summary named none; the backend door is not offered. */
  tenantName?: string;
  deployMs?: number;
  deployedAt: number;
}

/**
 * The way into a redeployed backend.
 *
 * JSON rather than a 302, unlike `/api/sessions/:id/xano`. That route answers a
 * same-origin navigation; this one answers a cross-origin fetch that carries a
 * bearer token, and a browser navigation cannot carry a header. Putting the
 * staff token in a query string instead would leak it into history and
 * referrers. The dashboard calls `window.open` on this, in the same click
 * handler, well inside the token's 60-second life.
 */
export interface StaffHandoff {
  url: string;
}

/** SSE event name carrying an `Activity` payload on the session stream. */
export const SSE_ACTIVITY_EVENT = "activity";

/** The prompt length ceiling, enforced identically on both sides. */
export const MAX_PROMPT_LENGTH = 5000;
